/**
 * Mob AI Config
 *
 * Aggro / leash / wander / attack tuning for hostile mobs. Per-mob TOML
 * data can override individual fields (a tank-class mob might have a
 * larger leash range), but anything unspecified per-mob falls through to
 * these defaults.
 *
 * Two-section file (defaults+dev-override pattern). See
 * `docs/plans/2026-04-25-CONFIG-MIGRATION.md`.
 */

export interface MobAiConfig {
  /** Pixels: player enters this range → mob aggros. */
  aggroRangePx: number;
  /** Lower bound for positive authored ranges, so short-range hostiles wake
   * before the player is already standing on top of them. */
  hostileAggroRangeMinPx: number;
  /** Pixels: if target > this from spawn, deaggro and walk home. */
  leashRangePx: number;
  /** Pixels: at this range, mob deals damage. */
  attackRangePx: number;
  /** Milliseconds between mob auto-attacks. */
  attackIntervalMs: number;
  /** Base damage per mob attack (before defense calc). */
  attackDamage: number;
  /** Horizontal impulse (px/s) the player is shoved by on a connecting mob
   *  swing. Stays well under MovementHandler's TELEPORT_GUARD so the
   *  client-applied velocity isn't rejected as a teleport. */
  knockbackImpulsePx: number;
  /** Client: how long (ms) the shove slides before ground friction resumes.
   *  180 px/s × 220 ms ≈ 40 px — a shove you can see (plan 23 row 55). */
  knockbackSlideMs: number;
  /** Per-frame chance (0–1) to pick a new wander direction. */
  wanderChance: number;
  /** Per-frame chance (0–1) to stop wandering. */
  wanderStopChance: number;
  /** Multiplier on `moveSpeed` while returning to spawn after deaggro. */
  returnSpeed: number;
  /** Milliseconds after death before the respawn timer starts. */
  deathDespawnMs: number;
  /** Milliseconds to respawn after despawn. */
  respawnMs: number;
  /** Pixels a calm mob may wander from its spawn point. */
  wanderRadiusPx: number;
  // ── Chase and engagement knobs (plan 08). Each can be overridden per mob in
  // TOML (chase_speed_mult, reaction_ms, …) or per behaviour template
  // (mob-ai-templates.config.ts); these are the global fallbacks.
  /** Pause between noticing a player and starting the chase. */
  reactionMs: number;
  /** Chase speed as a multiple of the mob's move speed. */
  chaseSpeedMult: number;
  /** Jump when the target stands this many pixels above the mob. */
  jumpThresholdPx: number;
  /** Same-family mobs within this radius join a fight (0 = never). */
  socialAggroRangePx: number;
  /** A chasing mob that gains less than `chaseProgressPx` in this long is
   *  treated as blocked and tries a jump. */
  chaseStuckMs: number;
  chaseProgressPx: number;
  /** Blocked jumps in a row before the mob gives up and leashes home. */
  chaseGiveUpJumps: number;
  // ── Skittish template ──
  /** Flee speed as a multiple of move speed. */
  fleeSpeedMult: number;
  /** A hit keeps a skittish mob running this long. */
  fleeDurationMs: number;
  /** A shockwave ability hits players whose feet are within this many px
   *  of the mob's feet — anyone in the air has jumped it. */
  shockwaveFloorTolerancePx: number;
}

export const MOB_AI_DEFAULTS: MobAiConfig = {
  aggroRangePx: 250,
  // Preserve authored boss/set-piece pulls while lifting the many 100–160px
  // common-mob ranges. A global multiplier made 300–400px bosses pull at
  // 450–500px, leaving too little room inside the 600px leash.
  hostileAggroRangeMinPx: 180,
  leashRangePx: 900,
  // 2026-06-09: re-tuned 20 → 32 for the new CENTER-to-center nearest-player
  // metric (MobAISystem.findNearestPlayer). Under the old top-left metric a
  // mob parked flush read ~40px away, so attackRangePx=20 could NEVER pass the
  // gate and mobs never swung. With centers, a flush melee mob sits at center
  // distance ≈ (mobHalfW + playerHalfW) ≈ 40-50px; effectiveRange =
  // attackRange + mob.width/4, gated at effectiveRange*1.5, so 32 lands a
  // touching swing while still reading as melee-range (not the old ranged
  // feel). Per-mob overrides (e.g. Pig Mom boss attackRange=120) stay intact
  // via MobLogic.attackRange. Tune live on /pve.
  attackRangePx: 32,
  // A 1.25s cadence is a measured 20% increase over the old 1.5s loop.
  // Ability-specific cooldowns and telegraph windups still govern specials.
  attackIntervalMs: 1250,
  attackDamage: 5,
  knockbackImpulsePx: 180,
  knockbackSlideMs: 220,
  wanderChance: 0.008,
  wanderStopChance: 0.01,
  returnSpeed: 1.5,
  deathDespawnMs: 3000,
  respawnMs: 10000,
  // Wander used to be bounded by half the leash (300px); kept at 300 when the
  // leash grew so calm mobs stay where map authors placed them.
  wanderRadiusPx: 300,
  // Plan 08 §2.2 starting defaults (DJ 2026-10-07: "a bit more aggressive").
  // Were: 300ms hard-coded reaction, plain move speed, 600px leash, jump only
  // when the target was >50px above, no social aggro.
  reactionMs: 150,
  chaseSpeedMult: 1.25,
  jumpThresholdPx: 30,
  // Small on purpose: starter packs help each other without pulling a field.
  socialAggroRangePx: 140,
  chaseStuckMs: 400,
  chaseProgressPx: 6,
  chaseGiveUpJumps: 3,
  fleeSpeedMult: 1.4,
  fleeDurationMs: 3000,
  shockwaveFloorTolerancePx: 24,
};

/**
 * Per-dev-environment overrides. Partial — anything omitted falls through
 * to MOB_AI_DEFAULTS.
 *
 * Examples:
 *   aggroRangePx: 50         // pacifist mobs for screenshot/cinematic flow
 *   respawnMs: 1000          // fast respawn for combat-loop iteration
 *   wanderChance: 0           // freeze mobs in place for ability accuracy QA
 */
export const MOB_AI_DEV_OVERRIDE: Partial<MobAiConfig> = {};

export const MOB_AI: MobAiConfig = {
  ...MOB_AI_DEFAULTS,
  ...MOB_AI_DEV_OVERRIDE,
};

/**
 * Convert content-authored proximity into the live hostile acquisition range.
 * `floorPx` is the behaviour template's aggro floor (mob-ai-templates.config.ts);
 * it defaults to the global hostile floor.
 *
 * This policy lives at the production spawn funnel rather than inside
 * MobAISystem.register(): low-level AI tests pass explicit effective ranges to
 * that method, while every real zone/map/debug/event spawn starts from authored
 * content. Keeping the conversion here preserves those test seams and makes
 * NPC.aggroRange publish the exact post-policy value the server enforces.
 */
export function resolveEffectiveMobAggroRange(
  authoredRange?: number,
  floorPx: number = MOB_AI.hostileAggroRangeMinPx,
): number {
  const base = authoredRange ?? MOB_AI.aggroRangePx;
  if (!Number.isFinite(base) || base <= 0) return 0;
  return Math.max(floorPx, base);
}
